/**
 * Andon Service — API client for the Andon Hub (flag listing, acknowledge, resolve)
 */
import FamlaServices from './FamlaServices';

export interface AndonFlag {
    id?: string;
    _id?: string;
    hostApp: string;
    userAzureId?: string;
    userEmail: string;
    userName: string;
    userMessage: string;
    contextText?: string;
    contextType?: string;
    subject?: string;
    status: 'pending' | 'acknowledged' | 'resolved' | string;
    acknowledgedBy?: string;
    acknowledgedAt?: string;
    resolvedBy?: string;
    resolvedAt?: string;
    resolutionNote?: string;
    notifiedAdmins?: number;
    notifiedAdminsCount?: number;
    companyId?: string;
    created_at?: string;
    createdAt?: string;
    updated_at?: string;
    updatedAt?: string;
}

export interface AcknowledgePayload {
    notes?: string;
    note?: string;
}

export interface ResolvePayload {
    note?: string;
    resolutionNotes?: string;
}

export function getAndonFlagId(flag: Pick<AndonFlag, 'id' | '_id'>): string {
    return flag.id || flag._id || '';
}

function normalizeFlag(raw: any): AndonFlag {
    if (!raw || typeof raw !== 'object') {
        return raw as AndonFlag;
    }
    const created = raw.createdAt || raw.created_at;
    return {
        ...raw,
        id: raw.id || raw._id,
        _id: raw._id || raw.id,
        created_at: created,
        createdAt: created,
        notifiedAdmins: raw.notifiedAdmins ?? raw.notifiedAdminsCount,
        resolutionNote: raw.resolutionNote || raw.resolution_notes,
    };
}

export async function listFlags(accessToken: string): Promise<AndonFlag[]> {
    const response = await FamlaServices(
        'api_host',
        '/andon/flags',
        'GET',
        '',
        accessToken
    );

    if (response.status !== 200) {
        throw new Error(response.body?.description || `Request failed: ${response.status}`);
    }

    const data = response.body?.data ?? response.body;
    const flags = Array.isArray(data)
        ? data
        : Array.isArray(data?.flags)
            ? data.flags
            : [];

    return flags.map(normalizeFlag);
}

export async function getFlag(id: string, accessToken: string): Promise<AndonFlag> {
    const response = await FamlaServices(
        'api_host',
        `/andon/flags/${encodeURIComponent(id)}`,
        'GET',
        '',
        accessToken
    );

    if (response.status !== 200) {
        throw new Error(response.body?.description || `Request failed: ${response.status}`);
    }

    return normalizeFlag(response.body?.data ?? response.body);
}

export async function acknowledgeFlag(
    id: string,
    data: AcknowledgePayload,
    accessToken: string
) {
    const payload = {
        notes: data.notes ?? data.note,
    };
    const response = await FamlaServices(
        'api_host',
        `/andon/${encodeURIComponent(id)}/acknowledge`,
        'PUT',
        payload,
        accessToken
    );

    if (response.status !== 200) {
        throw new Error(response.body?.description || `Request failed: ${response.status}`);
    }

    return response.body?.data ?? response.body;
}

export async function resolveFlag(
    id: string,
    data: ResolvePayload,
    accessToken: string
) {
    const payload = {
        resolutionNotes: data.resolutionNotes ?? data.note ?? '',
    };
    const response = await FamlaServices(
        'api_host',
        `/andon/${encodeURIComponent(id)}/resolve`,
        'PUT',
        payload,
        accessToken
    );

    if (response.status !== 200) {
        throw new Error(response.body?.description || `Request failed: ${response.status}`);
    }

    return response.body?.data ?? response.body;
}
